import React from 'react'
import { isNull } from 'lodash'
import { SortDirectionEnum, GetLogFiles_getLogFiles, useGetLogFile } from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Card from 'components/Card'
import { Box } from 'components/Layout'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { downloadFileFromUrl } from 'utils/helpers'

interface ColumnLabels {
  lab?: string | null
  date?: string | null
  actions?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  logs: GetLogFiles_getLogFiles[]
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
}

const defaultColumnLabels = {
  lab: 'Lab',
  date: 'Date',
  actions: 'Actions',
}

const PAGE_LENGTH = 10

export const ReportsLogsDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  logs,
  sorter,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])
  const [getLogFile, { loading: loadingDownloadLogFile }] = useGetLogFile({
    onCompleted: ({ getLogFile }) => {
      downloadFileFromUrl({
        url: getLogFile,
        filename: `download.log`,
      })
    },
  })

  const downloadLog = (url: string) => {
    void getLogFile({
      variables: {
        key: url,
      },
    })
  }

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={logs}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.lab) ? <Datalist.HeaderCell label={columnLabels.lab} /> : null}
              {!isNull(columnLabels.date) ? (
                <Datalist.HeaderCell label={columnLabels.date} />
              ) : null}
              {!isNull(columnLabels.actions) ? (
                <Datalist.HeaderCell label={columnLabels.actions} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const { lab, date, url } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.lab) ? (
                  <Datalist.Cell label={columnLabels.lab}>{lab}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.date) ? (
                  <Datalist.Cell label={columnLabels.date}>
                    <Box>{date}</Box>
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.actions) ? (
                  <Datalist.Cell label={columnLabels.actions}>
                    <Button
                      leftIcon={<Icon.CloudDownloadOutline size="S" />}
                      appearance="link"
                      size="small"
                      isFetching={loadingDownloadLogFile}
                      onClick={() => downloadLog(url)}
                    >
                      Download file
                    </Button>
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
    </>
  )
}
